/* eslint-disable complexity */
import { useDpvalePromotion } from '@/data/Content/Dpvale';
import { Divider, Skeleton, Stack, Typography } from '@mui/material';
import React, { FC, useContext, useMemo } from 'react';
import { ContentContext } from '@/data/context/content';
import { useProductDetails } from '@/data/Content/ProductDetails';
import { useSkuListTable } from '@/data/Content/SkuListTable';
import { isKitOrBundleType } from '@/utils/productIsA';
import { getProductDisplayInfo } from '@/utils/getProductDisplayInfo';
import { calculateProductPrices } from 'integration/utils/calculateProductPrices';
import { PriceDisplay } from '@/components/blocks/PriceDisplay';
import { LegendFooter } from './parts/LegendFooter';
import { PromotionDetails } from './parts/PromotionDetails';
import { NotPromotionScenery } from './parts/NotPromotionScenery';
import { LegendPromotionsConditions } from './parts/LegendPromotionsConditions';
import { NotDataScenery } from './parts/NotDataScenery';
import { Img } from '../../MaterialImage';
import { ErrorScenery } from './parts/ErrorScenery';
import { AdaptiveDialog } from '@/components/blocks/AdaptiveDialog';
import getValueByPathFromUnknown from 'integration/utils/getValueByPathFromUnknown';
import { Switch } from '@/utils/switch';
import { useCart } from '@/data/Content/Cart';
import { searchContainsElectronic } from '@/components/content/CheckOut/utils/searchContainsElectronic';
import { useOrderItemList, useOrderItemListRows } from '@/data/Content/OrderItemList';

enum ErrorCodeType {
	ERR_MAX_DPVALE_AMOUNT = 'ERR_MAX_DPVALE_AMOUNT',
	ERR_DP_INTERNAL_EXCEPTION = 'ERR_DP_INTERNAL_EXCEPTION',
	UNKNOWN = 'UNKNOWN',
}

export const ProductDetailsDPValeDisplayModal: FC<{
	isElectronicos?: boolean;
	orderId?: string;
	onClose: () => void;
}> = ({ orderId, isElectronicos, onClose }) => {
	const { selection, product } = useContext(ContentContext) as ReturnType<
		typeof useProductDetails
	> &
		ReturnType<typeof useSkuListTable>;

	const isKitOrBundle = isKitOrBundleType[product?.type ?? 'unknown'];
	const sku = !isKitOrBundle ? selection?.sku : product;
	const { offer, list } = useMemo<{
		offer: number;
		list: number;
		currency: string;
		min: number;
	}>(() => {
		const { prices } = getProductDisplayInfo(sku, product);
		const defaults = { offer: 0, list: 0, currency: '' };
		return { ...defaults, ...prices, min: sku?.productPrice?.min || 0 };
	}, [sku, product]);

	const { price } = calculateProductPrices({
		list,
		offer,
		ribbons: sku?.ribbons ?? [],
	});

	const { data: cartDetails } = useCart();

	const orderItemList = useOrderItemList(cartDetails?.orderItem ?? [], orderId ?? '');
	const orderItemListRows = useOrderItemListRows(orderItemList.data);

	const isElectronico = useMemo(() => {
		if ((cartDetails?.orderItem?.length || 0) === 0 && isElectronicos) {
			return true;
		}

		const containsElectronic = searchContainsElectronic(orderItemListRows.data);
		if ((cartDetails?.orderItem?.length || 0) > 0 && containsElectronic) {
			return true;
		}

		return false;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const dpValePromotionData = useMemo(() => {
		if (!product) {
			return undefined;
		}

		return {
			orderId: orderId || '',
			product: {
				amount: price.toString(),
				sku: product?.partNumber || '',
				isElectronico: isElectronico ? 'true' : 'false',
			},
		};
	}, [product, price, orderId, isElectronico]);

	const { data: promotionDpVale, isValidating } = useDpvalePromotion(dpValePromotionData);

	const { promotions, promotionsError } = promotionDpVale || {};

	const errorData = promotionsError?.error?.errors?.[0];
	const errorGetPromotion = !!errorData;

	const { normal, special, cart } = promotions || {
		normal: [],
		special: [],
		cart: { grandTotal: 0, hasElectronicos: false, isProductInCart: false },
	};

	const errorType = useMemo(() => {
		const errorKey = getValueByPathFromUnknown(promotionsError, [
			'error',
			'errors',
			0,
			'errorKey',
		]) as ErrorCodeType;

		if (!errorKey) {
			return null;
		}

		return Switch<ErrorCodeType>(errorKey)
			.case(ErrorCodeType.ERR_MAX_DPVALE_AMOUNT, () => ErrorCodeType.ERR_MAX_DPVALE_AMOUNT)
			.case(ErrorCodeType.ERR_DP_INTERNAL_EXCEPTION, () => ErrorCodeType.ERR_DP_INTERNAL_EXCEPTION)
			.defaultTo(() => ErrorCodeType.UNKNOWN);
	}, [promotionsError]);

	const EmptyCartScenery: FC = () => (
		<Stack mt={'12px'}>
			<Stack direction={'row'}>
				{normal && normal.length ? (
					<Typography
						component={'span'}
						sx={{ fontSize: 'var(--font-size-small)', fontWeight: 'normal' }}
					>
						{'Agregando este producto a tu carrito y'}{' '}
						<Typography
							component={'span'}
							sx={{ fontSize: 'var(--font-size-small)', fontWeight: 'bold' }}
						>
							{'comprando HOY'}
						</Typography>{' '}
						{', así te quedarían tus pagos, de acuerdo a las promociones vigentes:'}
					</Typography>
				) : null}
			</Stack>

			<LegendPromotionsConditions />

			<Divider sx={{ marginTop: '20px' }}></Divider>
			{normal && normal.length ? (
				<PromotionDetails
					plazo={normal[0]?.quincenas}
					pago={normal[0]?.pagoQuincenal}
					total={normal[0]?.pagoTotal}
					paymentDate={normal[0]?.paymentDate.string}
				/>
			) : null}
			<Divider sx={{ marginTop: '20px' }}></Divider>

			{special && special.length && !isElectronicos ? (
				<Stack>
					<Stack direction={'row'} mt={'12px'}>
						<Typography
							component={'span'}
							sx={{ fontSize: 'var(--font-size-small)', fontWeight: 'normal' }}
						>
							{'En caso de contar con la promoción'}{' '}
							<Typography
								component={'span'}
								sx={{ fontSize: 'var(--font-size-small)', fontWeight: 'bold' }}
							>
								{'comprando HOY y PAGA HASTA'}
							</Typography>{' '}
							{'activa, asi quedarían tus pagos.'}{' '}
							<Typography
								component={'span'}
								sx={{ fontSize: 'var(--font-size-small)', fontStyle: 'italic' }}
							>
								{'Usando esta promoción aceptas el cargo por promoción establecida.'}
							</Typography>{' '}
						</Typography>
					</Stack>
					<PromotionDetails
						plazo={special[0]?.quincenas}
						pago={special[0]?.pagoQuincenal}
						total={special[0]?.pagoTotal}
						paymentDate={special[0]?.paymentDate.string}
					/>
					<Divider sx={{ marginTop: '20px' }}></Divider>
				</Stack>
			) : null}
		</Stack>
	);

	const CartScenery: FC = () => (
		<Stack mt={'12px'}>
			{cart && cart.isProductInCart ? (
				<Stack direction={'row'}>
					<Typography
						component={'span'}
						sx={{ fontSize: 'var(--font-size-small)', fontWeight: 'normal' }}
					>
						{'Tienes'}{' '}
						<Typography
							component={'span'}
							sx={{ fontSize: 'var(--font-size-small)', fontWeight: 'bold' }}
						>
							<PriceDisplay
								currency={product?.productPrice?.currency}
								min={Number(cart.grandTotal)}
							/>
						</Typography>{' '}
						{'en tu carrito, '}{' '}
						<Typography
							component={'span'}
							sx={{ fontSize: 'var(--font-size-small)', fontWeight: 'bold' }}
						>
							{'si compras HOY'}
						</Typography>{' '}
						{', así te quedarían tus pagos, de acuerdo a las promociones vigentes:'}
					</Typography>
				</Stack>
			) : null}

			{cart && !cart.isProductInCart ? (
				<Stack direction={'row'}>
					<Typography
						component={'span'}
						sx={{ fontSize: 'var(--font-size-small)', fontWeight: 'normal' }}
					>
						{'Tienes'}{' '}
						<Typography
							component={'span'}
							sx={{ fontSize: 'var(--font-size-small)', fontWeight: 'bold' }}
						>
							<PriceDisplay
								currency={product?.productPrice?.currency}
								min={Number(cart.grandTotal)}
							/>
						</Typography>{' '}
						{'en tu carrito,'}{' '}
						<Typography
							component={'span'}
							sx={{ fontSize: 'var(--font-size-small)', fontStyle: 'italic' }}
						>
							{'agrega este producto para ver tu plan de pagos completo.'}
						</Typography>{' '}
						<Typography
							component={'span'}
							sx={{ fontSize: 'var(--font-size-small)', fontWeight: 'bold' }}
						>
							{'si compras HOY'}
						</Typography>{' '}
						{', así te quedarían tus pagos, de acuerdo a las promociones vigentes:'}
					</Typography>
				</Stack>
			) : null}

			<LegendPromotionsConditions />

			<Divider sx={{ marginTop: '20px' }}></Divider>
			{normal && normal.length ? (
				<PromotionDetails
					plazo={normal[0]?.quincenas}
					pago={normal[0]?.pagoQuincenal}
					total={normal[0]?.pagoTotal}
					paymentDate={normal[0]?.paymentDate.string}
					currency={product?.productPrice?.currency}
				/>
			) : null}
			<Divider sx={{ marginTop: '20px' }}></Divider>

			{special && special.length && !isElectronico && !cart?.hasElectronicos ? (
				<Stack>
					<Stack direction={'row'} mt={'12px'}>
						<Typography
							component={'span'}
							sx={{ fontSize: 'var(--font-size-small)', fontWeight: 'normal' }}
						>
							{'En caso de contar con la promoción'}{' '}
							<Typography
								component={'span'}
								sx={{ fontSize: 'var(--font-size-small)', fontWeight: 'bold' }}
							>
								{'comprando HOY y PAGA HASTA'}
							</Typography>{' '}
							{'activa, asi quedarían tus pagos.'}{' '}
							<Typography
								component={'span'}
								sx={{ fontSize: 'var(--font-size-small)', fontStyle: 'italic' }}
							>
								{'Usando esta promoción aceptas el cargo por promoción establecida.'}
							</Typography>{' '}
						</Typography>
					</Stack>
					<PromotionDetails
						plazo={special[0]?.quincenas}
						pago={special[0]?.pagoQuincenal}
						total={special[0]?.pagoTotal}
						paymentDate={special[0]?.paymentDate.string}
					/>
					<Divider sx={{ marginTop: '20px' }}></Divider>
				</Stack>
			) : null}
		</Stack>
	);

	return (
		<AdaptiveDialog onClose={onClose}>
			<Stack>
				<Stack display={'flex'} direction={'row'} alignItems={'center'}>
					<Typography sx={{ fontSize: 'var(--font-size-xl)', fontWeight: '800' }}>
						{'PLANES DE PAGO CON'}
					</Typography>
					<Img
						src="images/dpvale/dpvale@2x.png"
						alt="Dpvale"
						sx={{
							height: '26px',
							marginLeft: '4px',
							maxWidth: '146px',
						}}
					/>
				</Stack>
				{!promotionDpVale || isValidating || orderItemListRows.loading ? (
					<Stack gap="10px" mt={'10px'}>
						<Skeleton variant="rectangular" width="100%" height="15px" />
						<Skeleton variant="rectangular" width="90%" height="15px" />
						<Skeleton variant="rectangular" width="80%" height="15px" />
					</Stack>
				) : (
					<>
						{Switch(errorType)
							.case(ErrorCodeType.ERR_MAX_DPVALE_AMOUNT, () => (
								<ErrorScenery errorData={errorData!} currency={product?.productPrice?.currency} />
							))
							.case(ErrorCodeType.ERR_DP_INTERNAL_EXCEPTION, () => (
								<NotDataScenery showDpvalePromotion={!!normal?.length} />
							))
							.defaultTo(() => (
								<>
									{!errorData && !errorGetPromotion && !cart && normal ? (
										<EmptyCartScenery />
									) : null}
									{!errorData && !errorGetPromotion && cart && normal ? <CartScenery /> : null}
									{!errorData &&
									!errorGetPromotion &&
									!cart &&
									!normal?.length &&
									!special?.length ? (
										<NotPromotionScenery showDpvalePromotion={!!normal?.length} />
									) : null}
								</>
							))}
					</>
				)}
				<LegendFooter />
			</Stack>
		</AdaptiveDialog>
	);
};
